import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { EstimatedPaymentData } from 'entities/payment/api';
import { normalize } from 'normalizr';

import { networkSchema, normalizeEstimatedAmountByTokenId } from './normalize-schemes';
import { IPaymentNetwork, IPaymentToken, PaymentHdWallet, PaymentStatus, PaymentStatusState } from './payment.types';

export interface NormalizedToken extends IPaymentToken {
  networkId: string;
}

export type TokenId = string;

export interface NormalizedPaymentAssets {
  networks: {
    byId: Record<string, Omit<IPaymentNetwork, 'tokens'> & { tokens: string[] }>;
    allIds: string[];
  };
  tokens: {
    byId: Record<TokenId, NormalizedToken>;
    allIds: string[];
  };
}

export type PaymentEstimate = {
  amountToPay: Record<TokenId, number>;
  packageAvailable: number;
};

export type PaymentState = {
  payment: PaymentStatusState | null;
  paymentAssets: NormalizedPaymentAssets;
  paymentTokenId: string | null;
  paymentEstimate: PaymentEstimate;
  paymentHdWallets: PaymentHdWallet[];
};

const initialPaymentEstimateState = {
  amountToPay: {},
  packageAvailable: 0,
};

const initialPaymentState: PaymentState = {
  payment: null,
  paymentTokenId: null,
  paymentEstimate: initialPaymentEstimateState,
  paymentAssets: {
    networks: {
      byId: {},
      allIds: [],
    },
    tokens: {
      byId: {},
      allIds: [],
    },
  },
  paymentHdWallets: [],
};

const paymentSlice = createSlice({
  name: 'payment',
  initialState: initialPaymentState,
  reducers: {
    updatePaymentState: (state: PaymentState, action: PayloadAction<Partial<PaymentState>>) => ({
      ...state,
      ...action.payload,
    }),

    setPayment: (state, action: PayloadAction<PaymentStatusState | null>) => {
      state.payment = action?.payload;
    },

    resetPayment: (state) => {
      state.payment = {
        status: PaymentStatus.INITIAL,
        payload: null,
      };
    },

    setPaymentAssets(state: PaymentState, action: PayloadAction<IPaymentNetwork[]>) {
      const { entities, result } = normalize(action.payload, [networkSchema]);

      state.paymentAssets.networks.byId = entities.networks ?? {};
      state.paymentAssets.networks.allIds = result ?? [];

      state.paymentAssets.tokens.byId = entities.tokens ?? {};
      state.paymentAssets.tokens.allIds = Object.keys(entities.tokens ?? {});
    },

    setEstimatedPayment(state, { payload }: PayloadAction<EstimatedPaymentData>) {
      const normalizedAmounts = normalizeEstimatedAmountByTokenId(state.paymentAssets.tokens, payload.amountToPay);

      state.paymentEstimate = {
        amountToPay: normalizedAmounts,
        packageAvailable: payload.packageAvailable,
      };
    },

    setPaymentTokenId(state, action: PayloadAction<string>) {
      state.paymentTokenId = action.payload;
    },

    resetPaymentTokenId(state) {
      state.paymentTokenId = null;
    },

    clearPaymentState: () => ({
      ...initialPaymentState,
    }),

    clearUserPaymentState: (state) => ({
      ...state,
      payment: null,
      paymentTokenId: null,
      paymentEstimate: initialPaymentEstimateState,
      paymentHdWallets: [],
    }),
  },
});

export const { reducer } = paymentSlice;
export const { actions } = paymentSlice;
